import { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
	DEFAULT_PEOPLE_SEARCH_SORT,
	EMPTY_CRITERIA,
	PEOPLE_SEARCH_SORTS,
	PeopleSearchCategory,
	PeopleSearchCriteria,
	PeopleSearchSort,
	cloneCriteria,
	criteriaHash,
	getDirtyCategories,
	hasActiveCriteria,
} from "@/types/peopleSearch";
import { serializeCriteriaToURL, deserializeCriteriaFromURL } from "@/utils/peopleSearchSerialization";

interface PeopleSearchContextType {
	draft: PeopleSearchCriteria;
	applied: PeopleSearchCriteria;
	hasApplied: boolean;
	activeCategory: PeopleSearchCategory;
	setActiveCategory: (category: PeopleSearchCategory) => void;
	railFilterText: string;
	setRailFilterText: (text: string) => void;
	page: number;
	setPage: (page: number) => void;
	sort: PeopleSearchSort;
	setSort: (sort: PeopleSearchSort) => void;
	dirtyCategories: PeopleSearchCategory[];
	unappliedCount: number;
	updateDraft: <K extends PeopleSearchCategory>(category: K, slice: PeopleSearchCriteria[K]) => void;
	clearCategory: (category: PeopleSearchCategory) => void;
	apply: () => void;
	reset: () => void;
}

const PeopleSearchContext = createContext<PeopleSearchContextType | undefined>(undefined);

function parseCriteriaParam(raw: string | null): PeopleSearchCriteria {
	if (raw) {
		const parsed = deserializeCriteriaFromURL(raw);
		if (parsed)
			return parsed;
	}
	return cloneCriteria(EMPTY_CRITERIA);
}

function parsePageParam(raw: string | null): number {
	const parsed = parseInt(raw ?? '1', 10);
	return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
}

function parseSortParam(raw: string | null): PeopleSearchSort {
	return PEOPLE_SEARCH_SORTS.includes(raw as PeopleSearchSort)
		? (raw as PeopleSearchSort)
		: DEFAULT_PEOPLE_SEARCH_SORT;
}

export function PeopleSearchProvider({ children }: { children: ReactNode }) {
	const [searchParams, setSearchParams] = useSearchParams();

	// Initialize both draft and applied from the URL so shared links open pre-applied
	const initialCriteria = useMemo(() => parseCriteriaParam(searchParams.get('q')),
	// eslint-disable-next-line react-hooks/exhaustive-deps
	[]);

	const [draft, setDraft] = useState<PeopleSearchCriteria>(initialCriteria);
	const [applied, setApplied] = useState<PeopleSearchCriteria>(initialCriteria);
	const [hasApplied, setHasApplied] = useState(() => hasActiveCriteria(initialCriteria));
	const [activeCategory, setActiveCategory] = useState<PeopleSearchCategory>('role');
	const [railFilterText, setRailFilterText] = useState('');
	const [page, setPageState] = useState(() => parsePageParam(searchParams.get('p')));
	const [sort, setSortState] = useState<PeopleSearchSort>(() => parseSortParam(searchParams.get('s')));

	const dirtyCategories = useMemo(() => getDirtyCategories(draft, applied), [draft, applied]);

	const syncURL = useCallback((criteria: PeopleSearchCriteria, nextPage: number, nextSort: PeopleSearchSort, replace: boolean) => {
		const params = new URLSearchParams();
		if (hasActiveCriteria(criteria))
			params.set('q', serializeCriteriaToURL(criteria));
		if (nextPage > 1)
			params.set('p', String(nextPage));
		if (nextSort !== DEFAULT_PEOPLE_SEARCH_SORT)
			params.set('s', nextSort);
		setSearchParams(params, { replace });
	}, [setSearchParams]);

	// Restore state when the URL changes underneath us (browser back/forward).
	// The hash guard keeps our own syncURL writes from looping back into state.
	useEffect(() => {
		const urlCriteria = parseCriteriaParam(searchParams.get('q'));
		const urlPage = parsePageParam(searchParams.get('p'));
		const urlSort = parseSortParam(searchParams.get('s'));

		if (criteriaHash(urlCriteria) !== criteriaHash(applied)) {
			setApplied(cloneCriteria(urlCriteria));
			setDraft(cloneCriteria(urlCriteria));
			setHasApplied(hasActiveCriteria(urlCriteria));
		}
		if (urlPage !== page)
			setPageState(urlPage);
		if (urlSort !== sort)
			setSortState(urlSort);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams]);

	const setPage = useCallback((nextPage: number) => {
		setPageState(nextPage);
		syncURL(applied, nextPage, sort, true);
	}, [applied, sort, syncURL]);

	const setSort = useCallback((nextSort: PeopleSearchSort) => {
		setSortState(nextSort);
		setPageState(1);
		syncURL(applied, 1, nextSort, true);
	}, [applied, syncURL]);

	const updateDraft = useCallback(<K extends PeopleSearchCategory>(category: K, slice: PeopleSearchCriteria[K]) => {
		setDraft(prev => ({ ...prev, [category]: slice }));
	}, []);

	const clearCategory = useCallback((category: PeopleSearchCategory) => {
		const emptySlice = cloneCriteria(EMPTY_CRITERIA)[category];
		setDraft(prev => ({ ...prev, [category]: emptySlice }));
		setApplied(prev => {
			const next = { ...prev, [category]: emptySlice };
			syncURL(next, 1, sort, false);
			return next;
		});
		setPageState(1);
	}, [sort, syncURL]);

	const apply = useCallback(() => {
		const next = cloneCriteria(draft);
		setApplied(next);
		setHasApplied(true);
		setPageState(1);
		syncURL(next, 1, sort, false);
	}, [draft, sort, syncURL]);

	const reset = useCallback(() => {
		setDraft(cloneCriteria(EMPTY_CRITERIA));
		setApplied(cloneCriteria(EMPTY_CRITERIA));
		setPageState(1);
		syncURL(EMPTY_CRITERIA, 1, sort, false);
	}, [sort, syncURL]);

	return (
		<PeopleSearchContext.Provider
			value={{
				draft,
				applied,
				hasApplied,
				activeCategory,
				setActiveCategory,
				railFilterText,
				setRailFilterText,
				page,
				setPage,
				sort,
				setSort,
				dirtyCategories,
				unappliedCount: dirtyCategories.length,
				updateDraft,
				clearCategory,
				apply,
				reset,
			}}
		>
			{children}
		</PeopleSearchContext.Provider>
	);
}

export const usePeopleSearch = () => {
	const context = useContext(PeopleSearchContext);
	if (context === undefined)
		throw new Error("usePeopleSearch must be used within a PeopleSearchProvider");
	return context;
};
